import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type Pillar = {
  num: string;
  title: string;
  body: string;
  image_url: string;
};

export type IntroContent = {
  id: string;
  featured_label: string | null;
  rotating_words: string[];
  intro_headline: string | null;
  intro_body: string | null;
  pillars_heading: string | null;
  pillars: Pillar[];
  closer_intro_text: string | null;
  closer_text: string | null;
  lounge_image_url: string | null;
  reviews_kicker: string | null;
  reviews_subtitle: string | null;
};

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

const COLS =
  "id,featured_label,rotating_words,intro_headline,intro_body,pillars_heading,pillars,closer_intro_text,closer_text,lounge_image_url,reviews_kicker,reviews_subtitle";

function normalizePillars(value: unknown): Pillar[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((p) => {
      if (!p || typeof p !== "object") return null;
      const o = p as Record<string, unknown>;
      return {
        num: typeof o.num === "string" ? o.num : "",
        title: typeof o.title === "string" ? o.title : "",
        body: typeof o.body === "string" ? o.body : "",
        image_url: typeof o.image_url === "string" ? o.image_url : "",
      };
    })
    .filter((p): p is Pillar => p !== null);
}

export const getIntroPageContent = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => {
    if (!i) return {} as { experience_slug?: string };
    const o = i as { experience_slug?: string };
    return { experience_slug: o.experience_slug };
  })
  .handler(async ({ data }): Promise<IntroContent | null> => {
    const sb = publicClient();
    const expSlug = data?.experience_slug ?? null;
    const [c, ov, exp] = await Promise.all([
      sb
        .from("intro_content")
        .select(COLS)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle(),
      sb
        .from("page_overrides")
        .select("field_key,experience_id,value_text,value_url")
        .eq("page_key", "intro"),
      expSlug
        ? sb.from("experiences").select("id").eq("slug", expSlug).maybeSingle()
        : Promise.resolve({ data: null, error: null } as const),
    ]);
    if (c.error) throw new Error(c.error.message);
    if (ov.error) throw new Error(ov.error.message);
    if ("error" in exp && exp.error) throw new Error(exp.error.message);
    if (!c.data) return null;
    const raw = c.data as unknown as Record<string, unknown>;
    let content: IntroContent = {
      id: raw.id as string,
      featured_label: (raw.featured_label as string) ?? null,
      rotating_words: Array.isArray(raw.rotating_words)
        ? (raw.rotating_words as string[])
        : [],
      intro_headline: (raw.intro_headline as string) ?? null,
      intro_body: (raw.intro_body as string) ?? null,
      pillars_heading: (raw.pillars_heading as string) ?? null,
      pillars: normalizePillars(raw.pillars),
      closer_intro_text: (raw.closer_intro_text as string) ?? null,
      closer_text: (raw.closer_text as string) ?? null,
      lounge_image_url: (raw.lounge_image_url as string) ?? null,
      reviews_kicker: (raw.reviews_kicker as string) ?? null,
      reviews_subtitle: (raw.reviews_subtitle as string) ?? null,
    };
    const expId =
      ("data" in exp && exp.data ? (exp.data as { id: string }).id : null) ||
      null;
    if (expId) {
      const byField = new Map<string, { text: string | null; url: string | null }>();
      for (const o of ov.data ?? []) {
        if (o.experience_id === expId)
          byField.set(o.field_key, { text: o.value_text, url: o.value_url });
      }
      const pick = (key: string, fb: string | null) => {
        const v = byField.get(key);
        if (!v) return fb;
        return v.text ?? v.url ?? fb;
      };
      content = {
        ...content,
        featured_label: pick("featured_label", content.featured_label),
        intro_headline: pick("intro_headline", content.intro_headline),
        intro_body: pick("intro_body", content.intro_body),
        pillars_heading: pick("pillars_heading", content.pillars_heading),
        closer_intro_text: pick("closer_intro_text", content.closer_intro_text),
        closer_text: pick("closer_text", content.closer_text),
        lounge_image_url: pick("lounge_image", content.lounge_image_url),
        reviews_kicker: pick("reviews_kicker", content.reviews_kicker),
        reviews_subtitle: pick("reviews_subtitle", content.reviews_subtitle),
        rotating_words: (() => {
          const v = byField.get("rotating_words");
          if (!v?.text) return content.rotating_words;
          return v.text.split("\n").map((s) => s.trim()).filter(Boolean);
        })(),
        pillars: content.pillars.map((p, i) => {
          const n = i + 1;
          const t = byField.get(`pillar_${n}_title`);
          const b = byField.get(`pillar_${n}_body`);
          const img = byField.get(`pillar_${n}_image`);
          return {
            ...p,
            title: t?.text ?? p.title,
            body: b?.text ?? p.body,
            image_url: img?.url ?? p.image_url,
          };
        }),
      };
    }

    return content;
  });

const pillarSchema = z.object({
  num: z.string().max(8),
  title: z.string().max(120),
  body: z.string().max(600),
  image_url: z.string().nullable().transform((v) => v ?? ""),
});

const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  featured_label: z.string().nullable(),
  rotating_words: z.array(z.string().min(1)).max(40),
  intro_headline: z.string().nullable(),
  intro_body: z.string().nullable(),
  pillars_heading: z.string().nullable(),
  pillars: z.array(pillarSchema).max(12),
  closer_intro_text: z.string().nullable(),
  closer_text: z.string().nullable(),
  lounge_image_url: z.string().nullable(),
  reviews_kicker: z.string().nullable(),
  reviews_subtitle: z.string().nullable(),
});

export const upsertIntroContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => upsertSchema.parse(d))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const payload = {
      featured_label: data.featured_label,
      rotating_words: data.rotating_words,
      intro_headline: data.intro_headline,
      intro_body: data.intro_body,
      pillars_heading: data.pillars_heading,
      pillars: data.pillars,
      closer_intro_text: data.closer_intro_text,
      closer_text: data.closer_text,
      lounge_image_url: data.lounge_image_url,
      reviews_kicker: data.reviews_kicker,
      reviews_subtitle: data.reviews_subtitle,
    };
    if (data.id) {
      const { error } = await sb.from("intro_content").update(payload).eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await sb
      .from("intro_content")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });
